import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ACTIVE_STATUSES, type NotesDocument } from "@meeting-notes/shared";
import { useApi } from "../lib/api";
import { StatusChip } from "../components/StatusChip";
import { StageProgress } from "../components/StageProgress";
import { Transcript } from "../components/Transcript";

const tabs = ["요약", "안건", "노트", "F/U", "제안", "전사"] as const;
type Tab = (typeof tabs)[number];

export function MeetingPage() {
  const { id = "" } = useParams();
  const api = useApi();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("요약");
  const q = useQuery({
    queryKey: ["meeting", id],
    queryFn: () => api.getResult(id),
    refetchInterval: (query) => (query.state.data && ACTIVE_STATUSES.includes(query.state.data.meeting.status) ? 5_000 : false),
  });
  const del = useMutation({
    mutationFn: () => api.deleteMeeting(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["meetings"] });
      nav("/");
    },
  });

  if (q.isLoading) return <p className="p-4 text-slate-400">불러오는 중…</p>;
  if (q.error || !q.data) return <p className="p-4 text-red-300">{String((q.error as Error)?.message ?? "오류")}</p>;
  const { meeting, notes, transcriptUrl, audioUrl, notesMarkdownUrl } = q.data;
  const doc = notes as NotesDocument | null;
  const speakerLabels = Object.fromEntries((doc?.speakers ?? []).map((s) => [s.id, s.label]));

  return (
    <div className="px-4 pt-4">
      <button className="tap text-sm text-indigo-300 mb-2" onClick={() => nav(-1)}>‹ 목록</button>
      <div className="flex items-start justify-between gap-3">
        <h1 className="text-xl font-bold leading-snug">{meeting.title}</h1>
        <StatusChip status={meeting.status} />
      </div>
      <p className="mt-1 text-xs text-slate-400">{new Date(meeting.createdAt).toLocaleString("ko-KR")}{meeting.durationSec ? ` · ${Math.round(meeting.durationSec / 60)}분` : ""}</p>
      {meeting.status !== "COMPLETED" && (
        <div className="mt-4 rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <StageProgress meeting={meeting} />
          {meeting.status === "FAILED" && <p className="mt-3 text-sm text-red-300">{meeting.error}</p>}
          {ACTIVE_STATUSES.includes(meeting.status) && <p className="mt-3 text-xs text-slate-400">처리 중입니다. 완료되면 알림을 보내 드립니다.</p>}
        </div>
      )}
      {(doc || transcriptUrl) && (
        <div className="mt-4 flex gap-1 overflow-x-auto no-scrollbar">
          {tabs.map((t) => (
            <button key={t} className={`tap shrink-0 rounded-full px-3 py-1.5 text-sm ${tab === t ? "bg-indigo-500 text-white" : "bg-slate-900 text-slate-300"}`} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>
      )}
      <section className="mt-4">
        {tab === "요약" && doc && <SummaryTab doc={doc} />}
        {tab === "안건" && doc && <AgendaTab doc={doc} />}
        {tab === "노트" && doc && <NotesTab doc={doc} notesMarkdownUrl={notesMarkdownUrl} />}
        {tab === "F/U" && doc && <FollowUpsTab doc={doc} />}
        {tab === "제안" && doc && <SuggestionsTab doc={doc} />}
        {tab === "전사" && transcriptUrl && <Transcript transcriptUrl={transcriptUrl} audioUrl={audioUrl} speakerLabels={speakerLabels} />}
        {tab !== "전사" && !doc && <p className="text-sm text-slate-400">분석이 끝나면 여기에 결과가 표시됩니다.</p>}
      </section>
      <button className="tap mt-10 w-full rounded-xl border border-red-900 text-red-300 py-3 text-sm" disabled={del.isPending} onClick={() => { if (confirm("이 회의와 모든 결과를 삭제할까요?")) del.mutate(); }}>
        회의 삭제
      </button>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4 mb-3">
      <h3 className="font-semibold mb-2">{title}</h3>
      {children}
    </div>
  );
}
const Bullets = ({ items }: { items: string[] }) => (items.length ? <ul className="list-disc pl-5 space-y-1 text-sm text-slate-200">{items.map((x, i) => <li key={i}>{x}</li>)}</ul> : <p className="text-sm text-slate-500">없음</p>);

function SummaryTab({ doc }: { doc: NotesDocument }) {
  return (
    <div>
      <p className="text-base font-medium mb-3">{doc.summary.headline}</p>
      <Card title="개요"><p className="text-sm text-slate-200 whitespace-pre-line leading-relaxed">{doc.summary.overview}</p></Card>
      <Card title="참석자"><ul className="text-sm space-y-1">{doc.speakers.map((s) => <li key={s.id}><span className="font-medium">{s.label}</span>{s.role ? <span className="text-slate-400"> · {s.role}</span> : null}</li>)}</ul></Card>
      <Card title="결정 사항"><Bullets items={doc.summary.keyDecisions} /></Card>
      <Card title="핵심 논의"><ul className="space-y-2 text-sm">{doc.summary.keyDiscussions.map((d, i) => <li key={i}><span className="font-medium">{d.title}</span><p className="text-slate-300">{d.detail}</p></li>)}</ul></Card>
      <Card title="리스크·이슈"><Bullets items={doc.summary.risksAndIssues} /></Card>
      <Card title="다음 단계"><Bullets items={doc.summary.nextSteps} /></Card>
      <Card title="토픽 흐름"><ul className="text-sm space-y-1">{doc.topics.map((t) => <li key={t.id}><span className="text-slate-400">{Math.floor(t.startSec / 60)}분</span> {t.title}</li>)}</ul></Card>
    </div>
  );
}

function AgendaTab({ doc }: { doc: NotesDocument }) {
  return (
    <div>
      {doc.agenda.map((a) => (
        <Card key={a.id} title={`${a.id}. ${a.title}`}>
          <p className="text-sm text-slate-300 mb-2">{a.background}</p>
          <p className="text-xs text-slate-400 mb-1">논의</p><Bullets items={a.discussionPoints} />
          {a.decisions.length > 0 && <><p className="text-xs text-emerald-300 mt-2 mb-1">결정</p><Bullets items={a.decisions} /></>}
          {a.openQuestions.length > 0 && <><p className="text-xs text-amber-300 mt-2 mb-1">미결·질문</p><Bullets items={a.openQuestions} /></>}
        </Card>
      ))}
    </div>
  );
}

function NotesTab({ doc, notesMarkdownUrl }: { doc: NotesDocument; notesMarkdownUrl: string | null }) {
  return (
    <div>
      {doc.notes.sections.map((s, i) => <Card key={i} title={s.title}><Bullets items={s.bullets} /></Card>)}
      {notesMarkdownUrl && <a href={notesMarkdownUrl} className="tap block text-center text-sm text-indigo-300 py-2">전체 회의록(markdown) 열기</a>}
    </div>
  );
}

function FollowUpsTab({ doc }: { doc: NotesDocument }) {
  const pr = { high: "text-red-300", medium: "text-amber-300", low: "text-slate-400" } as const;
  return (
    <ul className="space-y-2">
      {doc.followUps.length === 0 && <p className="text-sm text-slate-500">F/U 항목이 없습니다.</p>}
      {doc.followUps.map((f) => (
        <li key={f.id} className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <p className="font-medium text-sm">{f.title}</p>
          <p className="mt-1 text-xs text-slate-400">
            <span className={pr[f.priority]}>● {f.priority}</span>
            {f.ownerName ? ` · 담당 ${f.ownerName}` : ""}{f.dueHint ? ` · 기한 ${f.dueHint}` : ""}{f.status === "carried_over" ? ` · 이월(${f.carriedFrom ?? "이전 회의"})` : ""}
          </p>
        </li>
      ))}
    </ul>
  );
}

function SuggestionsTab({ doc }: { doc: NotesDocument }) {
  return (
    <div>
      {doc.suggestions.map((s) => (
        <Card key={s.id} title={s.target.title}>
          <p className="text-sm text-slate-200 whitespace-pre-line leading-relaxed">{s.suggestion}</p>
          {s.nextSteps.length > 0 && <><p className="text-xs text-indigo-300 mt-3 mb-1">다음 단계</p><Bullets items={s.nextSteps} /></>}
          {s.alternatives.length > 0 && <><p className="text-xs text-slate-400 mt-3 mb-1">대안</p><Bullets items={s.alternatives} /></>}
          {s.risks.length > 0 && <><p className="text-xs text-amber-300 mt-3 mb-1">리스크</p><Bullets items={s.risks} /></>}
          {s.clarifyingQuestions.length > 0 && <><p className="text-xs text-slate-400 mt-3 mb-1">확인 질문</p><Bullets items={s.clarifyingQuestions} /></>}
          {s.conflictsWithPast && <p className="mt-3 text-xs text-red-300">과거 결정과 충돌: {s.conflictsWithPast}</p>}
        </Card>
      ))}
    </div>
  );
}
